import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation } from '../../../components/Location'
import { usePlacesWidget } from 'react-google-autocomplete'
import { ENV } from 'src/config/env'
import moment from 'moment'
import { postHttp } from 'src/api'
import { PATH_API, PATH_URL } from 'src/helpers'
import { useHistory } from 'react-router-dom'
import { getQueryParameters } from 'src/helpers/tool'
import { preserveUrlParams } from 'src/helpers/preserveUrlParams'
import { useAppContext } from 'src/helpers/app'
import './styles.scss'
import ProgressBar from '../ProgressBar'

interface DateFields {
  month: string
  day: string
  year: string
}

interface CheckAvailabilityRequest {
  eventDateValue: string
  placeId: string
  locationText: string
}

const DEFAULT_MIN_DAYS = 45
const DATE_FORMAT = 'YYYY-MM-DD'

const DatePickerPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<DateFields>({
    month: '',
    day: '',
    year: '',
  })
  const [dateError, setDateError] = useState('')
  const [addressError, setAddressError] = useState('')
  const [hasDateError, setHasDateError] = useState(false)
  const [venueMinDays, setVenueMinDays] = useState(DEFAULT_MIN_DAYS)
  const [isCheckingVenue, setIsCheckingVenue] = useState(false)
  const [selectedPlace, setSelectedPlace] = useState<any>(null)
  const [isAddressValid, setIsAddressValid] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const [checkSuccess, setCheckSuccess] = useState(false)
  const [isFormReady, setIsFormReady] = useState(false)
  const [addressInputValue, setAddressInputValue] = useState('')
  const [isPlaceSelected, setIsPlaceSelected] = useState(false) // Flag to track if a place was selected
  const history = useHistory()
  const { storeURLParameters } = useAppContext()

  const location = useLocation()
  const { handleGGPlaceSelected, handleChange, locationSelected } = location

  // Check for collectionId parameter and save it to collectionsQuizEntry
  useEffect(() => {
    const params = getQueryParameters()
    const collectionId = params.get('collectionId')

    if (collectionId) {
      console.log('Collection ID detected in DatePickerPage, saving to collectionsQuizEntry')
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
      collectionsQuizEntry.collectionId = collectionId
      localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))
    }
  }, [])

  const clearErrors = useCallback(() => {
    setDateError('')
    setAddressError('')
    setHasDateError(false)
  }, [])

  const validateDateFields = useCallback((): boolean => {
    const { month, day, year } = selectedDate
    if (!month || !day || !year || year.length !== 4) {
      return false
    }

    const paddedMonth = month.padStart(2, '0')
    const paddedDay = day.padStart(2, '0')
    const inputDate = moment(`${year}-${paddedMonth}-${paddedDay}`, DATE_FORMAT)
    const currentDate = moment().startOf('day')
    const daysDifference = inputDate.diff(currentDate, 'days')

    const validationRules = [
      {
        condition: !inputDate.isValid(),
        error: 'Please enter a valid date',
      },
      {
        condition: inputDate.isBefore(currentDate, 'day'),
        error: 'Please select a date in the future',
      },
      {
        condition: daysDifference < venueMinDays,
        error: `All new clients must be at least ${venueMinDays} days away from their event.`,
      },
    ]

    const failedValidation = validationRules.find((rule) => rule.condition)

    setDateError(failedValidation?.error || '')
    setHasDateError(!!failedValidation)
    return !failedValidation
  }, [selectedDate, venueMinDays])

  const formatDateForApi = useCallback((): string => {
    const { month, day, year } = selectedDate
    const paddedMonth = month.padStart(2, '0')
    const paddedDay = day.padStart(2, '0')
    return moment(`${year}-${paddedMonth}-${paddedDay}`, DATE_FORMAT).format(DATE_FORMAT)
  }, [selectedDate])

  const checkVenueAvailability = useCallback(
    async (place: any) => {
      const isValidDate = validateDateFields()

      if (!isValidDate) {
        setAddressError('Please enter a valid date before checking venue availability')
        return false
      }

      try {
        setIsCheckingVenue(true)
        const formattedDate = formatDateForApi()

        // Format request data according to the API requirements
        const requestData: CheckAvailabilityRequest = {
          eventDateValue: formattedDate,
          placeId: place.place_id || '',
          locationText: place.formatted_address || '',
        }

        const result = await postHttp(PATH_API.checkAvailability(), {
          ...requestData,
          urlParameters: storeURLParameters ?? undefined,
        })

        if (!result || !result.data) {
          throw new Error('Invalid response from server')
        }

        if (result.data.serviceable === false) {
          const errorMessage = `Sorry, we don't have enough time to prepare for your event.`
          setAddressError(errorMessage)
          setIsAddressValid(false)
          setSelectedPlace(null)
          return false
        }

        // Success case - venue is available
        clearErrors()
        setIsAddressValid(true)
        const minDays = result.data.minPreparationDays || DEFAULT_MIN_DAYS
        setVenueMinDays(minDays)
        return true
      } catch (error) {
        console.error('Error checking venue availability:', error)
        setAddressError('Failed to check venue availability. Please try again.')
        setVenueMinDays(DEFAULT_MIN_DAYS)
        setSelectedPlace(null)
        return false
      } finally {
        setIsCheckingVenue(false)
      }
    },
    [formatDateForApi, validateDateFields, clearErrors]
  )

  const parsePlaceToEventVenue = (place: any): any => {
    let streetNumber = '',
      route = '',
      city = '',
      state = '',
      zipCode = '',
      country = ''

    place?.address_components?.forEach((component: any) => {
      const componentType = component.types[0]
      switch (componentType) {
        case 'street_number':
          streetNumber = component.long_name
          break
        case 'route':
          route = component.long_name
          break
        case 'postal_code':
          zipCode = component.long_name
          break
        case 'locality':
          city = component.long_name
          break
        case 'administrative_area_level_1':
          state = component.short_name
          break
        case 'country':
          country = component.short_name
          break
      }
    })

    // Extract venue name more intelligently
    let venueName = place?.name || ''

    // If name is empty or is just the street address, try to extract from formatted_address
    if (!venueName || venueName === `${streetNumber} ${route}`.trim()) {
      // Try to get the first part of formatted_address before the first comma
      if (place?.formatted_address) {
        const firstPart = place.formatted_address.split(',')[0].trim()
        // Only use it if it's not just a street address
        if (firstPart && firstPart !== `${streetNumber} ${route}`.trim()) {
          venueName = firstPart
        }
      }
    }

    // Ensure we have at least basic location info
    if (!city && place?.formatted_address) {
      const addressParts = place.formatted_address.split(',').map((part: string) => part.trim())
      if (addressParts.length > 1) {
        // Try to extract city from formatted address
        const lastPart = addressParts[addressParts.length - 1]
        const stateZipMatch = lastPart.match(/([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?/)
        if (stateZipMatch) {
          state = stateZipMatch[1]
          zipCode = stateZipMatch[2] || zipCode
          city = addressParts[addressParts.length - 2] || city
        } else {
          city = lastPart
        }
      }
    }

    // Set default country if not found
    if (!country) {
      country = 'US'
    }

    return {
      type: 'CEREMONY',
      place: [place?.name, place?.formatted_address].filter((i) => i).join(', ') || '',
      venue: venueName,
      city: city || '',
      state: state || '',
      streetAddress: `${streetNumber} ${route}`.trim() || '',
      zipCode: zipCode || '',
      placeId: place?.place_id || '',
      country: country || 'US',
    }
  }

  const handlePlaceSelection = useCallback(
    (place: any) => {
      console.log('Selected place:', place)
      setSelectedPlace(place)
      handleGGPlaceSelected(place, 0)

      // Display the full formatted address for better user experience
      const displayValue = place?.formatted_address || place?.name || ''
      setAddressInputValue(displayValue)
      setIsPlaceSelected(true)

      setAddressError('')
      setIsAddressValid(false)
      setCheckSuccess(false)
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
      const eventVenueObj = parsePlaceToEventVenue(place)
      collectionsQuizEntry.eventVenues = [eventVenueObj]
      localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))
    },
    [handleGGPlaceSelected]
  )

  const { ref } = usePlacesWidget<HTMLInputElement>({
    apiKey: ENV.REACT_APP_GG_MAP_API_KEY,
    onPlaceSelected: handlePlaceSelection,
    options: {
      types: ['establishment', 'geocode'],
      fields: ['name', 'formatted_address', 'address_components', 'place_id', 'types'],
      componentRestrictions: { country: 'us' },
    },
  })

  const monthInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (monthInputRef.current) {
      monthInputRef.current.focus()
    }
  }, [])

  // Focus on address input when place is selected
  useEffect(() => {
    if (isPlaceSelected && ref && typeof ref === 'object' && ref.current) {
      setTimeout(() => {
        if (ref && typeof ref === 'object' && ref.current) {
          ref.current.focus()
        }
      }, 200)
    }
  }, [isPlaceSelected, ref])

  const handleDateChange = useCallback((field: keyof DateFields, value: string) => {
    const numericValue = value.replace(/\D/g, '')

    const dateFormatters = {
      month: (val: string) => {
        const monthNum = parseInt(val)
        if (!val) return ''
        if (val.length === 1) return val
        if (monthNum === 0) return '01'
        if (monthNum > 12) return '12'
        return monthNum < 10 ? `0${monthNum}` : val
      },
      day: (val: string) => {
        const dayNum = parseInt(val)
        if (!val) return ''
        if (val.length === 1) return val
        if (dayNum === 0) return '01'
        if (dayNum > 31) return '31'
        return dayNum < 10 ? `0${dayNum}` : val
      },
      year: (val: string) => val.slice(0, 4),
    }

    const formattedValue = dateFormatters[field](numericValue)
    setSelectedDate((prev) => ({ ...prev, [field]: formattedValue }))
    setIsAddressValid(false)
    setCheckSuccess(false)
  }, [])

  const handleInputBlur = useCallback((field: keyof DateFields) => {
    setSelectedDate((prev) => {
      const value = prev[field]
      if (value.length === 1) {
        return { ...prev, [field]: `0${value}` }
      }
      return prev
    })
  }, [])

  const handleInputFocus = useCallback(() => {
    setCheckSuccess(false)
  }, [])

  const handleDateInputFocus = useCallback(() => {
    setHasDateError(false)
    setCheckSuccess(false)
  }, [])

  const handleAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(e, 0)
      setAddressInputValue(e.target.value)

      // Reset place selection flag when user starts typing
      if (isPlaceSelected) {
        setIsPlaceSelected(false)
        setSelectedPlace(null)
      }

      if (e.target.value) {
        setAddressError('')
      }
      setIsAddressValid(false)
      setCheckSuccess(false)
    },
    [handleChange, isPlaceSelected]
  )

  useEffect(() => {
    const isDateFilled = !!(selectedDate.month && selectedDate.day && selectedDate.year?.length === 4)
    const hasSelectedPlace = selectedPlace?.formatted_address
    const hasManualAddress = locationSelected[0]?.place && locationSelected[0].place.trim() !== ''
    const hasAddressInput = addressInputValue.trim() !== ''
    const isAddressFilled = hasSelectedPlace || hasManualAddress || hasAddressInput

    setIsFormReady(isDateFilled && isAddressFilled)
  }, [selectedDate, selectedPlace, locationSelected, addressInputValue])

  useEffect(() => {
    const shouldValidate = selectedDate.month && selectedDate.day && selectedDate.year?.length === 4

    shouldValidate ? validateDateFields() : setHasDateError(false)
  }, [selectedDate, validateDateFields])

  const handleNext = useCallback(async () => {
    // Validate date
    if (!validateDateFields()) {
      setHasDateError(true)
      return
    }

    // Check address - improved logic to handle both manual input and Google Places selection
    const hasSelectedPlace = selectedPlace?.formatted_address && isPlaceSelected
    const hasManualAddress = locationSelected[0]?.place && locationSelected[0].place.trim() !== ''
    const hasAddressInput = addressInputValue.trim() !== ''

    if (!hasSelectedPlace && !hasManualAddress && !hasAddressInput) {
      setAddressError('Please enter a valid venue address')
      return
    }

    setIsChecking(true)
    try {
      // Check venue
      let isSuccess = false
      let eventVenueObj = null

      if (hasSelectedPlace) {
        // Use selected place from Google Places Autocomplete
        isSuccess = await checkVenueAvailability(selectedPlace)
        eventVenueObj = parsePlaceToEventVenue(selectedPlace)
      } else if (hasManualAddress) {
        // Use manually entered address from locationSelected
        const manualAddress = locationSelected[0]?.place || ''
        const manualVenue = locationSelected[0]?.venue || ''

        // Try to extract basic location info from manual input
        const addressParts = manualAddress.split(',').map((part: string) => part.trim())
        let extractedCity = ''
        let extractedState = ''
        let extractedZipCode = ''

        // Simple parsing for manual input
        if (addressParts.length > 1) {
          // Try to extract city and state from the last parts
          const lastPart = addressParts[addressParts.length - 1]
          const stateZipMatch = lastPart.match(/([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?/)
          if (stateZipMatch) {
            extractedState = stateZipMatch[1]
            extractedZipCode = stateZipMatch[2] || ''
            extractedCity = addressParts[addressParts.length - 2] || ''
          } else {
            // If no state/zip pattern, assume last part is city
            extractedCity = lastPart
          }
        } else if (addressParts.length === 1) {
          // If only one part, use it as both venue and city
          extractedCity = addressParts[0]
        }

        const mockPlace = {
          name: manualVenue || addressParts[0] || '',
          formatted_address: manualAddress,
          place_id: locationSelected[0]?.placeId || '',
          address_components: [
            // Add basic address components for manual input
            ...(extractedCity ? [{ long_name: extractedCity, types: ['locality'] }] : []),
            ...(extractedState ? [{ short_name: extractedState, types: ['administrative_area_level_1'] }] : []),
            ...(extractedZipCode ? [{ long_name: extractedZipCode, types: ['postal_code'] }] : []),
            { short_name: 'US', types: ['country'] },
          ],
        }
        isSuccess = await checkVenueAvailability(mockPlace)
        eventVenueObj = {
          type: 'CEREMONY',
          place: manualAddress,
          venue: manualVenue || addressParts[0] || '',
          city: extractedCity,
          state: extractedState,
          streetAddress: addressParts[0] || '',
          zipCode: extractedZipCode,
          placeId: locationSelected[0]?.placeId || '',
          country: 'US',
        }
      } else if (hasAddressInput) {
        // Improved handling for direct address input
        const addressParts = addressInputValue.split(',').map((part: string) => part.trim())
        let extractedCity = ''
        let extractedState = ''
        let extractedZipCode = ''

        // Parse address input
        if (addressParts.length > 1) {
          const lastPart = addressParts[addressParts.length - 1]
          const stateZipMatch = lastPart.match(/([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?/)
          if (stateZipMatch) {
            extractedState = stateZipMatch[1]
            extractedZipCode = stateZipMatch[2] || ''
            extractedCity = addressParts[addressParts.length - 2] || ''
          } else {
            extractedCity = lastPart
          }
        } else if (addressParts.length === 1) {
          extractedCity = addressParts[0]
        }

        const mockPlace = {
          name: addressParts[0] || '',
          formatted_address: addressInputValue,
          place_id: '',
          address_components: [
            ...(extractedCity ? [{ long_name: extractedCity, types: ['locality'] }] : []),
            ...(extractedState ? [{ short_name: extractedState, types: ['administrative_area_level_1'] }] : []),
            ...(extractedZipCode ? [{ long_name: extractedZipCode, types: ['postal_code'] }] : []),
            { short_name: 'US', types: ['country'] },
          ],
        }
        isSuccess = await checkVenueAvailability(mockPlace)
        eventVenueObj = {
          type: 'CEREMONY',
          place: addressInputValue,
          venue: addressParts[0] || '',
          city: extractedCity,
          state: extractedState,
          streetAddress: addressParts[0] || '',
          zipCode: extractedZipCode,
          placeId: '',
          country: 'US',
        }
      }

      if (isSuccess) {
        const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')

        const quizResponses = collectionsQuizEntry.quizResponses || []
        const eventDateIndex = quizResponses.findIndex((item: any) => item.key === 'EVENT_DATE')
        const updatedQuizResponses =
          eventDateIndex > -1
            ? quizResponses.map((item: any, index: number) =>
                index === eventDateIndex ? { ...item, values: formatDateForApi() } : item
              )
            : [...quizResponses, { key: 'EVENT_DATE', values: formatDateForApi() }]
        collectionsQuizEntry.eventVenues = [eventVenueObj]
        collectionsQuizEntry.quizResponses = updatedQuizResponses

        // Ensure collectionId is saved if present
        const params = getQueryParameters()
        const collectionId = params.get('collectionId')
        if (collectionId) {
          collectionsQuizEntry.collectionId = collectionId
        }
        localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))

        // Use preserveUrlParams to maintain query parameters
        const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_GUEST_COUNT)
        history.push(nextUrl)
      }
    } catch (error) {
      console.error('Error in handleNext:', error)
    } finally {
      setIsChecking(false)
    }
  }, [
    selectedPlace,
    isPlaceSelected,
    locationSelected,
    addressInputValue,
    formatDateForApi,
    history,
    validateDateFields,
    checkVenueAvailability,
  ])

  const handleAddressKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleNext()
      }
    },
    [handleNext]
  )

  const renderDateInput = (field: keyof DateFields, placeholder: string, maxLength: number) => (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      placeholder={placeholder}
      value={selectedDate[field]}
      onChange={(e) => handleDateChange(field, e.target.value)}
      onBlur={() => handleInputBlur(field)}
      onFocus={handleDateInputFocus}
      maxLength={maxLength}
      className={hasDateError ? 'error' : ''}
      ref={field === 'month' ? monthInputRef : undefined}
    />
  )

  return (
    <>
      <ProgressBar />
      <div className="date-picker-page">
        <div className="date-picker-page-title-mobile">When and where are you getting married?</div>
        <div className="left-section">
          <div className="date-picker-page-left">
            <span className="date-picker-page__title">When and where are you getting married?</span>
            <span className="date-picker-page__subtitle">Enter your wedding date and the location of your wedding</span>
          </div>
        </div>
        <div className="right-section">
          <div className="date-section">
            <div className="date-inputs">
              <div className={`date-input-group ${hasDateError ? 'has-error' : ''}`}>
                {renderDateInput('month', 'MM', 2)}
                <span className={hasDateError ? 'error' : ''}>/</span>
                {renderDateInput('day', 'DD', 2)}
                <span className={hasDateError ? 'error' : ''}>/</span>
                {renderDateInput('year', 'YYYY', 4)}
              </div>
              {dateError && (
                <div className="error-message">
                  <span>⚠︎</span> {dateError}
                </div>
              )}
            </div>
          </div>
          <div className="address-section">
            <div
              className={`floating-label-wrapper ${addressError ? 'has-error' : ''} ${
                isAddressValid ? 'is-valid' : ''
              }`}
            >
              <input
                ref={ref}
                type="text"
                className="address-input"
                placeholder="Type your wedding address here"
                value={addressInputValue}
                onChange={handleAddressChange}
                onFocus={handleInputFocus}
                onKeyDown={handleAddressKeyDown}
                disabled={isCheckingVenue}
              />
            </div>
            {addressError ? (
              <div className="error-message">
                <span>⚠︎</span> {addressError}
              </div>
            ) : isCheckingVenue ? (
              <div className="checking-message">Checking venue availability...</div>
            ) : null}
          </div>
        </div>
        <button
          className={`date-picker-page-button-mobile ${isFormReady ? 'has-success' : ''}`}
          onClick={handleNext}
          disabled={isChecking}
        >
          <span className="date-picker-page__button-text">{isChecking ? 'Checking...' : 'Next'}</span>
        </button>
      </div>

      <div className="continue-desktop-container">
        <div className="continue-desktop-wrapper">
          <button
            className={`continue-button ${isFormReady ? 'has-success' : ''}`}
            onClick={handleNext}
            disabled={isChecking}
          >
            <span className="date-picker-page__button-text">{isChecking ? 'Checking...' : 'NEXT'}</span>
          </button>
        </div>
      </div>
    </>
  )
}

export default DatePickerPage
